import { useLogout } from "@/api/mutations/auth";
import { useMe } from "@/api/queries/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { ACCESS_TOKEN } from "@/constants/auth";
import { ROLES } from "@/constants/users";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  LogOut,
  type LucideProps,
  Moon,
  ScrollText,
  Sun,
  Users,
} from "lucide-react";
import { type ForwardRefExoticComponent, type RefAttributes, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";

type SubItem = { title: string; url: string };

export const AppSidebar = () => {
  const { data: me } = useMe();
  const { mutateAsync: logoutAsync } = useLogout();
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  const logout = async () => {
    try {
      await logoutAsync();
      localStorage.removeItem(ACCESS_TOKEN);

      window.location.href = "/login";
      toast.success("Logged out successfully", {
        description: "You have been successfully logged out of your account.",
      });
    } catch {
      toast.error("Logout failed", {
        description: "An error occurred while logging out. Please try again.",
      });
    }
  };

  const items = useMemo(() => {
    const meRole = me?.data?.role;

    return [
      {
        title: "Dashboard",
        url: "/dashboard",
        icon: LayoutDashboard,
      },
      meRole === ROLES.ADMIN && {
        title: "Users",
        url: "/users",
        icon: Users,
      },
      meRole === ROLES.ADMIN && {
        title: "Logs",
        icon: ScrollText,
        subItems: [
          {
            title: "User Logs",
            url: "/logs/user-logs",
          },
          {
            title: "Login Logs",
            url: "/logs/login-logs",
          },
        ],
      },
    ].filter(Boolean) as (
      | {
          title: string;
          url: string;
          icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
          subItems?: never;
        }
      | {
          title: string;
          icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
          subItems: SubItem[];
          url?: never;
        }
    )[];
  }, [me]);

  const navLinkIsActive = (url: string) => location.pathname === url;

  const checkSubItems = (subItems: SubItem[]) => {
    return subItems.some((item) => navLinkIsActive(item.url));
  };

  const activeLinkClassName =
    "bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-sm hover:bg-sidebar-primary/90 hover:text-white active:bg-sidebar-primary/80 active:scale-[0.98] transition-all duration-150";

  return (
    <Sidebar variant="inset">
      <SidebarHeader className="border-b">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `flex items-center gap-2 px-2 py-3 ${isActive ? "font-medium" : ""}`
          }
        >
          <Avatar>
            <AvatarFallback>{me?.data?.username?.slice(0, 1).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold">{me?.data?.username}</p>
          </div>
        </NavLink>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <Collapsible
                  key={item.title}
                  defaultOpen={checkSubItems(item.subItems ?? [])}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    {item.subItems ? (
                      <>
                        <CollapsibleTrigger className="w-full">
                          <SidebarMenuButton className="cursor-pointer">
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.subItems.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.title}>
                                <NavLink
                                  to={subItem.url}
                                  className={({ isActive }) => (isActive ? "font-medium" : "")}
                                >
                                  <SidebarMenuSubButton
                                    className={cn({
                                      [activeLinkClassName]: navLinkIsActive(subItem.url),
                                    })}
                                  >
                                    <span>{subItem.title}</span>
                                  </SidebarMenuSubButton>
                                </NavLink>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </>
                    ) : (
                      <NavLink to={item.url} className="flex gap-2 items-center">
                        <SidebarMenuButton
                          className={cn({
                            [activeLinkClassName]: navLinkIsActive(item.url),
                          })}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </NavLink>
                    )}
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
            <SidebarMenuButton className="cursor-pointer">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem onClick={logout}>
            <SidebarMenuButton className="cursor-pointer">
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
